import i18n from 'i18next';
import { isPlainObject } from 'lodash';

import type * as Models from './models.ts';
import type { ScheduledEmail } from './models.ts';
import { call } from './utils.ts';

export const API_URL = (import.meta.env.DEV ? 'http://localhost:8000' : window.location.origin) + '/api';

export type QueryFilterValue = string | number | boolean | null | undefined;
export type QueryFilters = Record<string, QueryFilterValue | QueryFilterValue[]>;

// API parameters only. React Query options belong to the query observer.
export interface UrlOptions {
    include?: string[];
    exclude?: string[];
    page?: number;
    pageSize?: number;
    translatable?: boolean;
}

interface RequestOptions {
    params?: QueryFilters & UrlOptions;
    signal?: AbortSignal;
    headers?: Record<string, string>;
    body?: unknown;
    files?: Record<string, File>;
}

export interface ConversationInput {
    receiverId: string;
    eventId: string;
    text?: string;
    attachmentId?: string | null;
}

export type InputFile = {
    name: string;
    url: string;
};

export interface SearchFilter {
    value: string;
    count?: number;
}

export interface PaginatedResponse<T> {
    filters: Record<string, SearchFilter[]>;
    count: number;
    next: string | null;
    previous: string | null;
    results: T[];
}

export const isFile = (obj: unknown): obj is File => 'File' in window && obj instanceof File;
export const isBlob = (obj: unknown): obj is Blob => 'Blob' in window && obj instanceof Blob;

const mapKeysDeep = (value: unknown, convertKey: (key: string) => string): unknown => {
    if (Array.isArray(value)) return value.map((item) => mapKeysDeep(item, convertKey));
    if (!isPlainObject(value)) return value;
    return Object.fromEntries(
        Object.entries(value as Record<string, unknown>).map(([key, item]) => [
            convertKey(key),
            mapKeysDeep(item, convertKey),
        ])
    );
};

const deepCamelCaseToSnakeCase = (value: unknown): unknown =>
    mapKeysDeep(value, (key) =>
        // Preserve filter operators such as eventId__not.
        key
            .split('__')
            .map((part) =>
                part
                    .replace(/([A-Z])/g, '_$1')
                    .toLowerCase()
                    .replace(/^_/, '')
            )
            .join('__')
    );

const deepSnakeCaseToCamelCase = (value: unknown): unknown =>
    mapKeysDeep(value, (key) => key.replace(/(_\w)/g, (part) => part[1].toUpperCase()));

const extractFiles = (value: unknown): Record<string, File> =>
    Object.fromEntries(
        Object.entries((value ?? {}) as Record<string, unknown>).filter((entry): entry is [string, File] =>
            isFile(entry[1])
        )
    );

const getCookie = (name: string) => {
    const matches = document.cookie.match(
        new RegExp('(?:^|; )' + name.replace(/([\.$?*|{}\(\)\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)')
    );
    return matches ? decodeURIComponent(matches[1]) : undefined;
};

export class BaseAPIClient<T, Input = Partial<T>> {
    baseUrl = API_URL;
    resource = '';

    protected call(path: string, method = 'GET', options: RequestOptions = {}): Promise<unknown> {
        const { params = {}, ...request } = options;
        const { include, exclude, ...filters } = params;
        const parsedPath = new URL(path, this.baseUrl);
        if (include) parsedPath.searchParams.set('include', include.join(','));
        if (exclude) parsedPath.searchParams.set('exclude', exclude.join(','));
        const search = deepCamelCaseToSnakeCase(filters) as QueryFilters;
        for (const [key, value] of Object.entries(search)) {
            for (const item of Array.isArray(value) ? value : [value]) {
                if (item !== undefined && item !== null) parsedPath.searchParams.append(key, String(item));
            }
        }
        parsedPath.searchParams.set('language', i18n.language);
        return call(`${parsedPath.pathname}${parsedPath.search}`, method, {
            ...request,
            headers: { 'X-CSRFToken': getCookie('csrftoken') || '', ...request.headers },
        });
    }

    async list(
        filters: QueryFilters = {},
        options: UrlOptions = {},
        signal?: AbortSignal
    ): Promise<PaginatedResponse<T>> {
        return this.convertResponse(
            await this.call(`/${this.resource}`, 'GET', { params: { ...filters, ...options }, signal })
        );
    }

    async search(
        filters: QueryFilters = {},
        options: UrlOptions = {},
        signal?: AbortSignal
    ): Promise<PaginatedResponse<T>> {
        return this.convertResponse(
            await this.call(`/${this.resource}/search`, 'GET', { params: { ...filters, ...options }, signal })
        );
    }

    async get(id: string, options: UrlOptions = {}, signal?: AbortSignal): Promise<T> {
        return this.convertResponse(
            await this.call(`/${this.resource}/${id}`, 'GET', { params: { ...options }, signal })
        );
    }

    async create(data: Input, options: UrlOptions = {}): Promise<T> {
        const payload = deepCamelCaseToSnakeCase(data);
        return this.convertResponse(
            await this.call(`/${this.resource}`, 'POST', {
                body: payload,
                files: extractFiles(payload),
                params: { ...options },
            })
        );
    }

    async update(id: string, data: Partial<Input>, partial = true, options: UrlOptions = {}): Promise<T> {
        const payload = deepCamelCaseToSnakeCase(data);
        return this.convertResponse(
            await this.call(`/${this.resource}/${id}`, partial ? 'PATCH' : 'PUT', {
                body: payload,
                files: extractFiles(payload),
                params: { ...options },
            })
        );
    }

    async delete(id: string, options: UrlOptions = {}): Promise<null> {
        await this.call(`/${this.resource}/${id}`, 'DELETE', { params: { ...options } });
        return null;
    }

    // The sole response assertion at the JSON boundary; convert each object once.
    protected convertResponse<Response = T>(data: unknown): Response {
        return deepSnakeCaseToCamelCase(data) as Response;
    }
}

export interface LoginInput {
    email: string;
    password: string;
    remember?: boolean;
}

export interface SignupInput extends LoginInput {
    firstName?: string;
    lastName?: string;
    confirmPassword: string;
}

export interface NewPasswordInput {
    newPassword: string;
    confirmPassword: string;
}

class AuthClient extends BaseAPIClient<Models.User> {
    resource = 'auth';

    async validate(): Promise<Models.User | null> {
        return this.convertResponse(await this.call(`/${this.resource}/session`));
    }

    async login(data: LoginInput): Promise<void> {
        await this.call(`/${this.resource}/session`, 'POST', { body: deepCamelCaseToSnakeCase(data) });
    }

    async logout(): Promise<void> {
        await this.call(`/${this.resource}/session`, 'DELETE');
    }

    async startSupportSession(organizationId: string): Promise<void> {
        await this.call(`/${this.resource}/support-session`, 'POST', {
            body: deepCamelCaseToSnakeCase({ organizationId }),
        });
    }

    async endSupportSession(): Promise<void> {
        await this.call(`/${this.resource}/support-session`, 'DELETE');
    }

    async signup(data: SignupInput): Promise<Models.User> {
        return this.convertResponse(
            await this.call(`/${this.resource}/signup`, 'POST', { body: deepCamelCaseToSnakeCase(data) })
        );
    }

    async confirmEmail(token: string): Promise<void> {
        await this.call(`/${this.resource}/confirm-email/${token}`, 'POST');
    }

    async confirmInvitation(token: string, data: NewPasswordInput): Promise<void> {
        await this.call(`/${this.resource}/confirm-invitation/${token}`, 'POST', {
            body: deepCamelCaseToSnakeCase(data),
        });
    }

    async forgotPassword(data: { email: string }): Promise<void> {
        await this.call(`/${this.resource}/forgot-password`, 'POST', { body: deepCamelCaseToSnakeCase(data) });
    }

    async resetPassword(token: string, data: NewPasswordInput): Promise<void> {
        await this.call(`/${this.resource}/reset-password/${token}`, 'POST', { body: deepCamelCaseToSnakeCase(data) });
    }
}

class SpacesClient extends BaseAPIClient<Models.Space> {
    resource = 'spaces';

    getLimits(id: string): Promise<Models.SpaceLimits> {
        return this.call(`/${this.resource}/${id}/limits`, 'GET').then((data: unknown) =>
            this.convertResponse<Models.SpaceLimits>(data)
        );
    }
}

class OrganizationsClient extends BaseAPIClient<
    Models.Organization,
    Partial<Models.TranslatableOrganization> & { logoId?: string | null; bannerId?: string | null }
> {
    resource = 'organizations';

    getLimits(id: string): Promise<Models.OrganizationLimits> {
        return this.call(`/${this.resource}/${id}/limits`, 'GET').then((data: unknown) =>
            this.convertResponse<Models.OrganizationLimits>(data)
        );
    }
}

class OrganizationProfilesClient extends BaseAPIClient<
    Models.OrganizationProfile,
    Partial<Models.TranslatableOrganizationProfile> & { logoId?: string | null; bannerId?: string | null }
> {
    resource = 'organization-profiles';

    getLimits(id: string): Promise<Models.OrganizationProfileLimits> {
        return this.call(`/${this.resource}/${id}/limits`, 'GET').then((data: unknown) =>
            this.convertResponse<Models.OrganizationProfileLimits>(data)
        );
    }
}

class OrganizationProfileJobsClient extends BaseAPIClient<Models.OrganizationProfileJob> {
    resource = 'organization-profile-jobs';
}

class OrganizationProfileUsersClient extends BaseAPIClient<Models.OrganizationProfileUser> {
    resource = 'organization-profile-users';
}

class JobsClient extends BaseAPIClient<Models.Job, Partial<Models.TranslatableJob>> {
    resource = 'jobs';
}

class UsersClient extends BaseAPIClient<Models.User, Partial<Models.TranslatableUser> & { imageId?: string | null }> {
    resource = 'users';

    updatePassword(id: string, data: NewPasswordInput & { oldPassword: string }): Promise<unknown> {
        return this.call(`/${this.resource}/${id}/password`, 'POST', {
            body: deepCamelCaseToSnakeCase(data),
        }).then((data: unknown) => this.convertResponse(data));
    }

    resendInvitation(id: string): Promise<unknown> {
        return this.call(`/${this.resource}/${id}/resend-invitation`, 'POST').then((data: unknown) =>
            this.convertResponse(data)
        );
    }

    resendConfirmEmail(id: string): Promise<unknown> {
        return this.call(`/${this.resource}/${id}/resend-confirm-email`, 'POST').then((data: unknown) =>
            this.convertResponse(data)
        );
    }
}

class UserProfilesClient extends BaseAPIClient<
    Models.UserProfile,
    Partial<Models.TranslatableUserProfile> & { cvId?: string | null }
> {
    resource = 'user-profiles';
}

class EducationsClient extends BaseAPIClient<Models.Education, Partial<Models.TranslatableEducation>> {
    resource = 'educations';
}

class ExperiencesClient extends BaseAPIClient<Models.Experience, Partial<Models.TranslatableExperience>> {
    resource = 'experiences';
}

class UserNotificationSettingsClient extends BaseAPIClient<Models.UserNotificationSettings> {
    resource = 'user-notification-settings';
}

class UserNotesClient extends BaseAPIClient<Models.UserNote> {
    resource = 'user-notes';
}

class EventsClient extends BaseAPIClient<
    Models.Event,
    Partial<Models.TranslatableEvent> & {
        logoId?: string | null;
        bannerId?: string | null;
        bannerLogoId?: string | null;
    }
> {
    resource = 'events';

    getLimits(id: string): Promise<Models.EventLimits> {
        return this.call(`/${this.resource}/${id}/limits`, 'GET').then((data: unknown) =>
            this.convertResponse<Models.EventLimits>(data)
        );
    }

    getAnalytics(id: string): Promise<Models.EventAnalytics> {
        return this.call(`/${this.resource}/${id}/analytics`, 'GET').then((data: unknown) =>
            this.convertResponse<Models.EventAnalytics>(data)
        );
    }
}

class EventAttendeesClient extends BaseAPIClient<Models.EventAttendee> {
    resource = 'event-attendees';
}

class EventSessionsClient extends BaseAPIClient<Models.EventSession, Partial<Models.TranslatableEventSession>> {
    resource = 'event-sessions';
}

class EventSessionUsersClient extends BaseAPIClient<Models.EventSessionUser> {
    resource = 'event-session-users';
}

class EventPageClient extends BaseAPIClient<Models.EventPage, Partial<Models.TranslatableEventPage>> {
    resource = 'event-pages';
}

class ConversationsClient extends BaseAPIClient<Models.Conversation, ConversationInput> {
    resource = 'conversations';

    markRead(id: string): Promise<unknown> {
        return this.call(`/${this.resource}/${id}/mark-read`, 'POST').then((data: unknown) =>
            this.convertResponse(data)
        );
    }

    report(id: string, data: { targetUserId: string }): Promise<unknown> {
        return this.call(`/${this.resource}/${id}/report`, 'POST', {
            body: deepCamelCaseToSnakeCase(data),
        }).then((data: unknown) => this.convertResponse(data));
    }

    block(id: string): Promise<unknown> {
        return this.call(`/${this.resource}/${id}/block`, 'POST').then((data: unknown) => this.convertResponse(data));
    }

    unblock(id: string): Promise<unknown> {
        return this.call(`/${this.resource}/${id}/unblock`, 'POST').then((data: unknown) => this.convertResponse(data));
    }
}

class MessagesClient extends BaseAPIClient<Models.Message, Partial<Models.Message> & { attachmentId?: string | null }> {
    resource = 'messages';
}

class SavedUsersClient extends BaseAPIClient<Models.SavedUser> {
    resource = 'saved-users';
}

class SavedOrganizationsClient extends BaseAPIClient<Models.SavedOrganization> {
    resource = 'saved-organizations';
}

class SavedJobsClient extends BaseAPIClient<Models.SavedJob> {
    resource = 'saved-jobs';
}

class SavedEventSessionsClient extends BaseAPIClient<Models.SavedEventSession> {
    resource = 'saved-event-sessions';
}

class LogEventsClient extends BaseAPIClient<Models.LogEvent> {
    resource = 'reporting/log-events';

    trackingStatus(): Promise<{ trackingAccepted: boolean | null }> {
        return this.call(`/${this.resource}/tracking-status`, 'GET').then((data: unknown) =>
            this.convertResponse<{ trackingAccepted: boolean | null }>(data)
        );
    }

    acceptTracking(): Promise<{ trackingAccepted: boolean | null }> {
        return this.call(`/${this.resource}/accept-tracking`, 'POST').then((data: unknown) =>
            this.convertResponse<{ trackingAccepted: boolean | null }>(data)
        );
    }

    rejectTracking(): Promise<{ trackingAccepted: boolean | null }> {
        return this.call(`/${this.resource}/reject-tracking`, 'POST').then((data: unknown) =>
            this.convertResponse<{ trackingAccepted: boolean | null }>(data)
        );
    }
}

class FileClient extends BaseAPIClient<Models.UploadedFile> {
    resource = 'files';
}

interface EmailPreviewData {
    subject: string;
    text: string;
    html: string;
    fromEmail: string;
    fromName: string;
    replyTo?: string;
}

class ScheduledEmailClient extends BaseAPIClient<Models.ScheduledEmail> {
    resource = 'scheduled-emails';

    preview(data: Partial<ScheduledEmail>): Promise<EmailPreviewData> {
        const payload = deepCamelCaseToSnakeCase(data);
        return this.call(`/${this.resource}/preview`, 'POST', {
            body: payload,
        }).then((data: unknown) => this.convertResponse<EmailPreviewData>(data));
    }

    sendPreview(data: Partial<ScheduledEmail>): Promise<unknown> {
        const payload = deepCamelCaseToSnakeCase(data);
        return this.call(`/${this.resource}/send-preview`, 'POST', {
            body: payload,
        }).then((data: unknown) => this.convertResponse(data));
    }
}

class SuggestionsClient extends BaseAPIClient<Models.Suggestion> {
    resource = 'common/suggestions';
}

class API {
    public auth: AuthClient;
    public spaces: SpacesClient;
    public organizations: OrganizationsClient;
    public organizationProfiles: OrganizationProfilesClient;
    public organizationProfileJobs: OrganizationProfileJobsClient;
    public organizationProfileUsers: OrganizationProfileUsersClient;
    public jobs: JobsClient;
    public users: UsersClient;
    public userProfiles: UserProfilesClient;
    public educations: EducationsClient;
    public experiences: ExperiencesClient;
    public userNotificationSettings: UserNotificationSettingsClient;
    public userNotes: UserNotesClient;
    public events: EventsClient;
    public eventAttendees: EventAttendeesClient;
    public eventSessions: EventSessionsClient;
    public eventSessionUsers: EventSessionUsersClient;
    public eventPages: EventPageClient;
    public conversations: ConversationsClient;
    public messages: MessagesClient;
    public savedUsers: SavedUsersClient;
    public savedOrganizations: SavedOrganizationsClient;
    public savedJobs: SavedJobsClient;
    public savedEventSessions: SavedEventSessionsClient;
    public logEvents: LogEventsClient;
    public files: FileClient;
    public scheduledEmails: ScheduledEmailClient;
    public suggestions: SuggestionsClient;

    constructor() {
        this.auth = new AuthClient();
        this.spaces = new SpacesClient();
        this.organizations = new OrganizationsClient();
        this.organizationProfiles = new OrganizationProfilesClient();
        this.organizationProfileJobs = new OrganizationProfileJobsClient();
        this.organizationProfileUsers = new OrganizationProfileUsersClient();
        this.jobs = new JobsClient();
        this.users = new UsersClient();
        this.userProfiles = new UserProfilesClient();
        this.educations = new EducationsClient();
        this.experiences = new ExperiencesClient();
        this.userNotificationSettings = new UserNotificationSettingsClient();
        this.userNotes = new UserNotesClient();
        this.events = new EventsClient();
        this.eventAttendees = new EventAttendeesClient();
        this.eventSessions = new EventSessionsClient();
        this.eventSessionUsers = new EventSessionUsersClient();
        this.eventPages = new EventPageClient();
        this.conversations = new ConversationsClient();
        this.messages = new MessagesClient();
        this.savedUsers = new SavedUsersClient();
        this.savedOrganizations = new SavedOrganizationsClient();
        this.savedJobs = new SavedJobsClient();
        this.savedEventSessions = new SavedEventSessionsClient();
        this.logEvents = new LogEventsClient();
        this.files = new FileClient();
        this.scheduledEmails = new ScheduledEmailClient();
        this.suggestions = new SuggestionsClient();
    }
}

export default new API();
